import { cn } from "@/lib/utils";

interface LogoAnimationProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: number;
  /** Full animation cycle duration in seconds */
  duration?: number;
  /** "pulse" = dots light up clockwise · "spin" = dots grow/shrink in diagonal pairs while the whole group rotates */
  variant?: "pulse" | "spin";
}

// Same 2x2 circle layout as Logo, animated in one of two ways to form a loading spinner.
export const LogoAnimation = ({
  size = 32,
  duration = 1.2,
  variant = "pulse",
  ...rest
}: LogoAnimationProps) => {
  const circleSize = size / 2;
  const delayStep = duration / 4;

  if (variant === "spin") {
    const spinDot = (grow: boolean) => (
      <div
        className={cn(
          "rounded-full bg-primary",
          grow ? "logo-animation-grow" : "logo-animation-shrink",
          rest.className
        )}
        style={{
          width: circleSize,
          height: circleSize,
          animationDuration: `${duration}s`,
        }}
      />
    );

    return (
      <div
        className={cn("flex gap-0 flex-shrink-0 logo-animation-spin")}
        style={{
          width: size,
          height: size,
          animationDuration: `${duration * 2}s`,
        }}
        role="status"
        aria-label="Loading"
        {...rest}
      >
        <div className="flex flex-col gap-0">
          {spinDot(true)}
          {spinDot(false)}
        </div>
        <div className="flex flex-col gap-0">
          {spinDot(false)}
          {spinDot(true)}
        </div>

        <style>{`
          @keyframes logo-animation-rotate {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          @keyframes logo-animation-grow-shrink {
            0%, 100% { transform: scale(0.7); opacity: 0.5; }
            50% { transform: scale(1.1); opacity: 1; }
          }
          .logo-animation-spin {
            animation-name: logo-animation-rotate;
            animation-timing-function: linear;
            animation-iteration-count: infinite;
          }
          .logo-animation-grow,
          .logo-animation-shrink {
            animation-name: logo-animation-grow-shrink;
            animation-timing-function: ease-in-out;
            animation-iteration-count: infinite;
          }
          .logo-animation-shrink {
            animation-direction: reverse;
          }
        `}</style>
      </div>
    );
  }

  const dot = (delay: number) => (
    <div
      className={cn(
        "rounded-full bg-primary logo-animation-dot",
        rest.className
      )}
      style={{
        width: circleSize,
        height: circleSize,
        animationDuration: `${duration}s`,
        animationDelay: `${delay}s`,
      }}
    />
  );

  return (
    <div
      className={cn("flex gap-0 flex-shrink-0")}
      style={{ width: size, height: size }}
      role="status"
      aria-label="Loading"
    >
      <div className="flex flex-col gap-0">
        {dot(0)}
        {dot(delayStep * 3)}
      </div>
      <div className="flex flex-col gap-0">
        {dot(delayStep)}
        {dot(delayStep * 2)}
      </div>

      <style>{`
        @keyframes logo-animation-pulse {
          0%, 100% {
            opacity: 0.35;
            transform: scale(0.7);
          }
          25% {
            opacity: 1;
            transform: scale(1);
          }
        }
        .logo-animation-dot {
          animation-name: logo-animation-pulse;
          animation-timing-function: ease-in-out;
          animation-iteration-count: infinite;
        }
      `}</style>
    </div>
  );
};
